/**
 * Translation (P0-BR4, NFR-6). Bangla is the default locale.
 *
 * Deliberately small rather than pulling in i18next. The whole requirement is
 * flat keys, `{{count}}` interpolation and one/other plurals, which is about
 * forty lines — and every kilobyte here is downloaded over 3G on a mid-range
 * Android phone (NFR-1, NFR-3).
 *
 * The real benefit is that keys are typed from en.json, so a missing or
 * misspelled key is a compile error rather than the literal string
 * "auth.login.titel" appearing on a partner's screen.
 */

import bn from './bn.json';
import en from './en.json';

export const LOCALES = { bn, en } as const;
export type Locale = keyof typeof LOCALES;

/** Every key defined in English. Bangla is checked against it by a test. */
export type TranslationKey = keyof typeof en;

export const DEFAULT_LOCALE: Locale = 'bn';

export interface TranslateOptions {
  /** Selects the _one/_other variant and fills {{count}}. */
  count?: number;
  /** Any other {{placeholder}} values. */
  [placeholder: string]: string | number | undefined;
}

/**
 * Picks the plural variant for a key.
 *
 * Bangla and English share the same one/other split, so one rule covers both.
 * A key with no variants is returned unchanged.
 */
function resolveKey(table: Record<string, string>, key: string, count?: number): string | undefined {
  if (count !== undefined) {
    const variant = `${key}_${count === 1 ? 'one' : 'other'}`;
    if (table[variant] !== undefined) return table[variant];
  }
  return table[key];
}

const interpolate = (template: string, values: TranslateOptions): string =>
  template.replace(/\{\{(\w+)\}\}/g, (whole, name: string) => {
    const value = values[name];
    return value === undefined ? whole : String(value);
  });

/**
 * Translates a key.
 *
 * Falls back to English if a Bangla string is missing, and to the key itself if
 * neither has it. Returning the key is deliberate: a partner seeing
 * "auth.login.title" reports a visible bug, whereas an empty string looks like
 * a rendering glitch nobody mentions.
 */
export function translate(locale: Locale, key: TranslationKey | string, options: TranslateOptions = {}): string {
  const table = LOCALES[locale] as Record<string, string>;
  const fallback = LOCALES.en as Record<string, string>;

  const template = resolveKey(table, key, options.count) ?? resolveKey(fallback, key, options.count);
  if (template === undefined) return key;

  return interpolate(template, options);
}

/** Builds a translator bound to one locale, for components. */
export const translatorFor =
  (locale: Locale) =>
  (key: TranslationKey | string, options?: TranslateOptions): string =>
    translate(locale, key, options);

/** The label shown on the language toggle, always in its own language. */
export const LOCALE_LABELS: Record<Locale, string> = { bn: 'বাংলা', en: 'English' };
